/**
 * Aggregates all portfolio data from various backend hooks
 */

import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useBackendAccount } from "@/backend/hooks/use-backend-account";
import { useBackendPositions } from "@/backend/hooks/use-backend-positions";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { FillsApi, DepositsApi, WithdrawalsApi, FundingApi, OrdersApi } from "@/backend/api-client";
import type { Fill, Deposit, Withdrawal, FundingPayment, Order } from "@/backend/types";
import { type DateRangeValue, getDateRange } from "@/components/ui/date-range-selector";

type UsePortfolioDataOptions = {
  dateRange?: DateRangeValue;
};

export const usePortfolioData = (options: UsePortfolioDataOptions = {}) => {
  const { dateRange = "all" } = options;
  const { userId } = useUnifiedAuth();

  // Account summary
  const { summary: accountSummary, isLoading: accountLoading } = useBackendAccount(userId ?? undefined);

  // Positions
  const { positions, isLoading: positionsLoading } = useBackendPositions(userId ?? undefined);

  // Get date range for server-side filtering (more efficient)
  const { from: fromDate, to: toDate } = useMemo(() => {
    if (dateRange === "all") return { from: null, to: null };
    return getDateRange(dateRange);
  }, [dateRange]);

  // Fills (trade history) - now with server-side date filtering
  const { data: rawFills = [], isLoading: fillsLoading } = useQuery({
    queryKey: ["fills", userId, dateRange],
    queryFn: () => {
      if (!userId) return [];
      // Use server-side filtering when date range is specified
      const from = fromDate?.toISOString();
      const to = toDate?.toISOString();
      return FillsApi.listAllFills(from, to);
    },
    enabled: !!userId,
    staleTime: 30000,
  });

  // Deposits
  const { data: rawDeposits = [], isLoading: depositsLoading } = useQuery({
    queryKey: ["deposits", userId],
    queryFn: () => (userId ? DepositsApi.listDeposits() : []),
    enabled: !!userId,
    staleTime: 30000,
  });

  // Withdrawals
  const { data: rawWithdrawals = [], isLoading: withdrawalsLoading } = useQuery({
    queryKey: ["withdrawals", userId],
    queryFn: () => (userId ? WithdrawalsApi.listWithdrawals() : []),
    enabled: !!userId,
    staleTime: 30000,
  });

  // Funding payments
  const { data: rawFundingPayments = [], isLoading: fundingLoading } = useQuery({
    queryKey: ["funding-payments", userId],
    queryFn: () => (userId ? FundingApi.listPayments() : []),
    enabled: !!userId,
    staleTime: 30000,
  });

  // Orders (for order history)
  const { data: rawOrders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ["orders-history", userId],
    queryFn: () => (userId ? OrdersApi.listOrders() : []),
    enabled: !!userId,
    staleTime: 30000,
  });

  // Filter data by date range (for APIs without server-side filtering)
  const filterByTime = useMemo(() => {
    // "all" means no filtering
    if (dateRange === "all") return null;
    return fromDate;
  }, [dateRange, fromDate]);

  // Fills are now filtered server-side, no need for client-side filtering
  const fills = useMemo(() => {
    return rawFills as Fill[];
  }, [rawFills]);

  const deposits = useMemo(() => {
    const typedDeposits = rawDeposits as Deposit[];
    if (!filterByTime) return typedDeposits;
    return typedDeposits.filter(
      (deposit) => new Date(deposit.created_at) >= filterByTime
    );
  }, [rawDeposits, filterByTime]);

  const withdrawals = useMemo(() => {
    const typedWithdrawals = rawWithdrawals as Withdrawal[];
    if (!filterByTime) return typedWithdrawals;
    return typedWithdrawals.filter(
      (withdrawal) => new Date(withdrawal.created_at) >= filterByTime
    );
  }, [rawWithdrawals, filterByTime]);

  const fundingPayments = useMemo(() => {
    const typedPayments = rawFundingPayments as FundingPayment[];
    if (!filterByTime) return typedPayments;
    return typedPayments.filter(
      (payment) => new Date(payment.created_at) >= filterByTime
    );
  }, [rawFundingPayments, filterByTime]);

  const orders = useMemo(() => {
    const typedOrders = rawOrders as Order[];
    // Filter for completed orders only
    const completedOrders = typedOrders.filter(
      (o) => o.status === "filled" || 
             o.status === "cancelled" || 
             o.status === "rejected" ||
             o.status === "partially_filled"
    );
    if (!filterByTime) return completedOrders;
    return completedOrders.filter(
      (order) => new Date(order.created_at) >= filterByTime
    );
  }, [rawOrders, filterByTime]);

  const isLoading =
    accountLoading ||
    positionsLoading ||
    fillsLoading ||
    depositsLoading ||
    withdrawalsLoading ||
    fundingLoading ||
    ordersLoading;

  return {
    userId,
    accountSummary,
    positions,
    fills,
    deposits,
    withdrawals,
    fundingPayments,
    orders,
    isLoading,
  };
};
